import { useCallback } from "react";

import { useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router";

import { TaskMetadataReason, TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";

import { useStartNextCheck } from "./useStartNextCheck";
import { getTaskPriorityGroup } from "../models/check/serializer";
import { useGetPendingChecksQuery } from "../queries/check/check.generated";
import { getInitialCheckUrl } from "../utils/checkUrl";

type UseChecksListProps = {
  type: TaskType[];
  metadataReason?: TaskMetadataReason[];
  origin: PageName;
};

export function useChecksList({ type, metadataReason, origin }: UseChecksListProps) {
  const navigate = useNavigate();
  const { startNextCheck, isLoading: isLoadingFetchNextCheckAndStartCheck } = useStartNextCheck();

  const {
    data,
    loading: isLoadingChecks,
    refetch: refetchChecks,
  } = useGetPendingChecksQuery({
    fetchPolicy: "network-only",
    variables: { input: { type, metadataReason } },
  });

  const {
    isOpen: isTaskIsTakenModalOpen,
    onOpen: onOpenTaskIsTakenModal,
    onClose: onCloseTaskIsTakenModal,
  } = useDisclosure();

  const {
    isOpen: isErrorModalOpen,
    onOpen: onOpenErrorModal,
    onClose: onCloseErrorModal,
  } = useDisclosure();

  const startCheck = useCallback(
    async (shelfNumber: string, priority: number) => {
      const groupedPriority = getTaskPriorityGroup(type, priority);
      try {
        const response = await startNextCheck({
          shelfNumber,
          groupedPriority,
          type,
        });
        if (response?.status === "checkAlreadyTaken") {
          onOpenTaskIsTakenModal();
          return;
        }
        if (response?.status === "noMoreCheck") {
          refetchChecks();
          return;
        }
        if (response?.check?.checkId && response?.check?.type) {
          const { type: checkType, checkId } = response.check;
          navigate(getInitialCheckUrl({ type: checkType, checkId, metadataReason }), {
            state: { origin },
          });
        }
      } catch (error) {
        onOpenErrorModal();
      }
    },
    [
      metadataReason,
      navigate,
      onOpenErrorModal,
      onOpenTaskIsTakenModal,
      origin,
      refetchChecks,
      startNextCheck,
      type,
    ],
  );

  return {
    isLoadingChecks,
    refetchChecks,
    rawChecks: data?.getPendingChecks?.checks ?? [],
    isLoadingFetchNextCheckAndStartCheck,
    isTaskIsTakenModalOpen,
    onCloseTaskIsTakenModal,
    startCheck,
    isErrorModalOpen,
    onCloseErrorModal,
  };
}
